import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { PackageDetail } from '../_common/interfaces'
import { Dayjs } from 'dayjs'
import { mercuryApi } from '$api'
import useAppStore from '$appStore'
import { exceptionMessageHandler, logger, PermissionType } from '$utils'
import { enqueueLoadedSnackbar } from 'src/utils/snackbar'
import { ClockinDetail, StaffMemberDetail } from '$models'

interface TimeclockSharedState {
  allClockins: ClockinDetail[]
  staffMembers: StaffMemberDetail[]
  allPackages: PackageDetail[]
}

export interface TimeclockSharedSlice extends TimeclockSharedState {
  setAllClockins: (newData: ClockinDetail[]) => void
  setStaffMembers: (newData: StaffMemberDetail[]) => void
  setAllPackages: (newData: PackageDetail[]) => void

  /**
   * Resets Timeclock Shared Slice state
   */
  resetTimeclockSharedSlice: () => void

  /**
   * Gets time clockins within a certain time range
   * @param startTime the start of the time range
   * @param endTime the end of the time range
   * @param includeInactive whether or not to include inactive clocks
   */
  getTimeClockins: (
    startDateTime: Dayjs,
    endDateTime: Dayjs,
    includeInactive: boolean
  ) => Promise<void>

  /**
   * Get Staff Members
   * @param time Today's date (not UTC)
   */
  getStaffMembers: (time?: Dayjs) => Promise<void>

  /**
   * Get All Packages
   */
  getPackages: () => Promise<void>

  /**
   * Update ClockinDetail
   * @param clockinDetail ClockinDetail to update
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   */
  updateClockinDetail: (
    clockinDetail: ClockinDetail,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>
}

const initialState: TimeclockSharedState = {
  allClockins: [],
  staffMembers: [],
  allPackages: [],
}

const createPunchesByStaffSlice: StateCreator<
  TimeclockSharedSlice & SharedSlice,
  [],
  [],
  TimeclockSharedSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  setAllClockins: (newData) => set(() => ({ allClockins: newData })),
  setStaffMembers: (newData) => set(() => ({ staffMembers: newData })),
  setAllPackages: (newData) => set(() => ({ allPackages: newData })),
  resetTimeclockSharedSlice: () => set(initialState),

  // State API calls
  getTimeClockins: async (
    startDateTime: Dayjs,
    endDateTime: Dayjs,
    includeInactive: boolean
  ): Promise<void> => {
    return mercuryApi
      .get<ClockinDetail[]>(
        `/time-clockins?startTime=${startDateTime
          .utc()
          .format()}&endTime=${endDateTime
          .utc()
          .format()}&includeInactive=${includeInactive}`
      )
      .then((result) => set(() => ({ allClockins: result })))
      .catch((error) => {
        set(() => ({
          allClockins: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve time clockins')
      })
  },

  getStaffMembers: async (time?: Dayjs): Promise<void> => {
    const queryString = time ? `time=${time.utc().format()}` : ''

    return mercuryApi
      .get<StaffMemberDetail[]>(`/staff-members?${queryString}`)
      .then((result) => set(() => ({ staffMembers: result })))
      .catch((error) => {
        set(() => ({
          staffMembers: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
  },

  getPackages: async (): Promise<void> => {
    const { activeCompany } = useAppStore.getState()

    const hasPayrollPermission = activeCompany?.hasPermission(
      PermissionType.Payroll
    )

    if (!hasPayrollPermission) {
      return set(() => ({ allPackages: [] }))
    }

    return mercuryApi
      .get<PackageDetail[]>(`/pay-rules/packages`)
      .then((result) => set(() => ({ allPackages: result })))
      .catch((error) => {
        set(() => ({
          allPackages: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
  },

  updateClockinDetail: async (
    clockinDetail: ClockinDetail,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      // TAKEN FROM ANGULAR:
      // Small hack for getting around the problem where changing the hourlyRate updates
      // the newClock's hourlyRate from undefined to null.
      // the latter will trigger a validation error
      // See LOADED-1226
      if (clockinDetail.hourlyRate == null) {
        delete clockinDetail.hourlyRate
      }

      const updatedClockinDetail: ClockinDetail = await mercuryApi.update(
        `/time-clockins/${clockinDetail.id}`,
        clockinDetail
      )
      if (!updatedClockinDetail) {
        logger.error(`${errorMessage} - updatedClockinDetail doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return false
      }

      set((state) => ({
        allClockins: state.allClockins.map((clockin) =>
          clockin.id === updatedClockinDetail.id
            ? updatedClockinDetail
            : clockin
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      return true
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return false
    } finally {
      set(() => ({ isLoading: false }))
    }
  },
})

export default createPunchesByStaffSlice
